<template>
    <div id="lt-yantr" class="advanced-calc debt-management-advisor-calc">
        <div class="debt-management-advisor-dark-section">
            <div class="container-width">
                <h4>{{ calcSubTitleDescription }}</h4>
                <debt-advisor-options
                    :section1-headers-and-sub-headers="Section1HeadersAndSubHeaders"
                    :section1-default-values="Section1DefaultValues"
                    :section1-input-labels="Section1InputLabels"
                    @dataLoaded="dataLoaded"
                    @receiveApiData="getAPIData"
                    @seeDetailsClicked="seeDetailsClicked"
                />
            </div>
        </div>
        <debt-advisor-tabbed-section
            v-show="isDataLoaded"
            :mapping-data="Section3Data"
            :section3-headers-and-sub-headers="Section3HeadersAndSubHeaders"
        />
        <debt-advisor-tabular
            v-show="isDataLoaded"
            :savings-json="SavingsJson"
            :dial-selection="DialData ? DialData.debtOptionName : ''"
            :section4-headers-and-sub-headers="Section4HeadersAndSubHeaders"
        />
    </div>
</template>

<script>
    import DebtAdvisorOptions from './components/debt-management-advisor-options.vue';
    import DebtAdvisorTabular from './components/debt-management-advisor-tabular.vue';
    import DebtAdvisorTabbedSection from './components/debt-management-advisor-tabbed-section.vue';
    import BaseMixin from '~/mixin/base-mixin';
    import DebtManagementAdvisorMixin from '~/mixin/debt-management-advisor';

    export default {
        props: {
            calcSubTitleDescription: {
                default: 'Choosing the right debt management option can add up to big savings. Calculate how much time and money you can save with different debt management options.',
            },
            labelCreditCardDebt: {
                default: 'Credit Card Debt',
            },
            labelPersonalLoan: {
                default: 'Personal Loans',
            },
            labelMedicalExpenses: {
                default: 'Medical Expenses',
            },
            labelOtherDebt: {
                default: 'Other Debt',
            },
            labelCreditRating: {
                default: 'Credit Score',
            },
            labelState: {
                default: 'State',
            },
            labelTerm: {
                default: 'Term',
            },
            labelRemainingTerm: {
                default: 'Remaining Term',
            },
            labelMinimumPayment: {
                default: 'Minimum Payment',
            },
            labelMonthlyPayment: {
                default: 'Monthly Payment',
            },
            labelApr: {
                default: 'APR',
            },
            updateButtonText: {
                default: 'Calculate',
            },
            showAdvancedOptions: {
                default: false,
            },
            labelDebtSettlement: {
                default: 'Savings',
            },
            labelDebtSettlementFees: {
                default: 'Fees',
            },
            labelCurrentStatusDebtAmount: {
                default: 'Debt (PL, CC)',
            },
            labelCurrentStatusInterestRate: {
                default: 'Interest',
            },
            labelCurrentStatusMonthlyPayment: {
                default: 'Mo. Payment',
            },
            labelCurrentStatusDebtFreeBy: {
                default: 'Debt Free By',
            },
            labelCurrentStatusDueNow: {
                default: 'Due Now',
            },
            labelCurrentTotalDebt: {
                default: 'Total Debt',
            },
            advancedFiltersHeader: {
                default: 'Current Debt Details',
            },
            advancedCreditCardHeader: {
                default: 'Credit Card',
            },
            advancedPersonalLoanHeader: {
                default: 'Personal Loan',
            },
            advancedSavingsHeader: {
                default: 'Savings Assumptions',
            },
            advancedDebtSettlementHeader: {
                default: 'Debt Settlement',
            },
            advancedHomeEquityHeader: {
                default: 'Home Equity',
            },
            step1Text: {
                default: 'Enter your current debt and click Update. Modify existing payments using Advanced Options, if needed.',
            },
            step2Text: {
                default: 'Click on Update to see your savings.',
            },
            step3Text: {
                default: 'Results updated, see your savings.',
            },
            creditScore: {
                default: 'Fair',
            },
            state: {
                default: 'NC',
            },
            creditCardDebt: {
                default: 10000,
            },
            personalLoanDebt: {
                default: 5000,
            },
            medicalDebt: {
                default: 0,
            },
            otherDebt: {
                default: 0,
            },
            debtSettlementRate: {
                default: 30.000,
            },
            debtSettlementFees: {
                default: 15.000,
            },
            creditCardMinimumPaymentPercentage: {
                default: 4.000,
            },
            personalLoanMinimumPaymentPercentage: {
                default: 3.000,
            },
            creditcardAprPercentage: {
                default: 19.990,
            },
            personalLoanAprPercentage: {
                default: 9.990,
            },
            personalLoanRemainingTerm: {
                default: 36,
            },
            homeEquityAprPercentage: {
                default: 4.990,
            },
            homeEquityTerm: {
                default: 120,
            },
            rentOrOwnSelection: {
                default: 'Own',
            },
            section2OptionInfo1: {
                default: 'Debt Covered',
            },
            section2OptionInfo2: {
                default: 'Not Covered',
            },
            section2OptionInfo3: {
                default: 'Mo. Payment',
            },
            dialSeeDetailsButtonText: {
                default: 'See Details',
            },
            dialNewPaymentText: {
                default: 'New Monthly Payment: ',
            },
            dialSeeOffersButtonText: {
                default: 'See your offers',
            },
            section3HeaderText: {
                default: 'Time vs. Money',
            },
            section3SubHeaderText: {
                default: 'Everyone has their most important factor when it comes to debt relief, some people want it done the fastest, others want the lowest payment, and some want the lowest overall cost. See how your options above compare by choosing your most important factor.',
            },
            section3Tab1HeaderText: {
                default: 'Time to be Debt Free',
            },
            section3Tab2HeaderText: {
                default: 'Monthly Payment',
            },
            section3Tab3HeaderText: {
                default: 'Highest Savings',
            },
            section4HeaderText: {
                default: 'Savings Breakdown',
            },
            section4SubHeaderText: {
                default: 'Here’s how each option stacks up in terms of overall savings. Adjustments made above will be reflected here.',
            },
            section4SeeDetailsButtonText: {
                default: 'See Details',
            },
            section4TableHeaderText1: {
                default: 'Debt Covered:',
            },
            section4TableHeaderText2: {
                default: 'Debt Not Covered:',
            },
            section4TableHeaderText3: {
                default: 'Interest:',
            },
            section4TableHeaderText4: {
                default: 'Mo. Payment:',
            },
            section4TableHeaderText5: {
                default: 'Est. Savings:',
            },
            section4TableCurrentDebtCoveredHeaderText: {
                default: 'Current Debt Covered',
            },
            mobSection4TableHeaderText1: {
                default: 'Debt Covered',
            },
            mobSection4TableHeaderText2: {
                default: 'Est. Interest',
            },
            mobSection4TableHeaderText3: {
                default: 'Mo. Payment',
            },
            section4SubTableHeaderText1: {
                default: 'Debt Type',
            },
            section4SubTableHeaderText2: {
                default: 'Debt Amount',
            },
            section4SubTableHeaderText3: {
                default: 'Est. Interest',
            },
            section4SubTableHeaderText4: {
                default: 'Mo. Payment',
            },
            isOtherDebtVisible: {
                default: '1',
            },
            labelRentOrOwn: {
                default: 'Rent Or Own',
            },
        },
        data() {
            return {
                APIData: null,
                isDataLoaded: true,
                DialData: null,
                Section1HeadersAndSubHeaders: {
                    filterHeader: this.filterHeader,
                    advancedFiltersHeader: this.advancedFiltersHeader,
                    advancedCreditCardHeader: this.advancedCreditCardHeader,
                    advancedPersonalLoanHeader: this.advancedPersonalLoanHeader,
                    advancedSavingsHeader: this.advancedSavingsHeader,
                    advancedDebtSettlementHeader: this.advancedDebtSettlementHeader,
                    advancedHomeEquityHeader: this.advancedHomeEquityHeader,
                },
                Section1InputLabels: {
                    labelCreditCardDebt: this.labelCreditCardDebt,
                    labelPersonalLoan: this.labelPersonalLoan,
                    labelMedicalExpenses: this.labelMedicalExpenses,
                    labelOtherDebt: this.labelOtherDebt,
                    labelCreditRating: this.labelCreditRating,
                    labelState: this.labelState,
                    labelTerm: this.labelTerm,
                    labelRemainingTerm: this.labelRemainingTerm,
                    labelMinimumPayment: this.labelMinimumPayment,
                    labelMonthlyPayment: this.labelMonthlyPayment,
                    labelAPR: this.labelApr,
                    updateButtonText: this.updateButtonText,
                    labelDebtSettlement: this.labelDebtSettlement,
                    labelDebtSettlementFees: this.labelDebtSettlementFees,
                    labelCurrentStatusDebtAmount: this.labelCurrentStatusDebtAmount,
                    labelCurrentStatusInterestRate: this.labelCurrentStatusInterestRate,
                    labelCurrentStatusMonthlyPayment: this.labelCurrentStatusMonthlyPayment,
                    labelCurrentStatusDebtFreeBy: this.labelCurrentStatusDebtFreeBy,
                    labelCurrentStatusDueNow: this.labelCurrentStatusDueNow,
                    labelCurrentTotalDebt: this.labelCurrentTotalDebt,
                    step1Text: this.step1Text,
                    step2Text: this.step2Text,
                    step3Text: this.step3Text,
                    section2OptionInfo1: this.section2OptionInfo1,
                    section2OptionInfo2: this.section2OptionInfo2,
                    section2OptionInfo3: this.section2OptionInfo3,
                    dialSeeDetailsButtonText: this.dialSeeDetailsButtonText,
                    dialNewPaymentText: this.dialNewPaymentText,
                    dialSeeOffersButtonText: this.dialSeeOffersButtonText,
                    labelRentOrOwn: this.labelRentOrOwn,
                },
                Section1DefaultValues: {
                    creditScore: this.creditScore,
                    state: this.state,
                    creditCardDebt: this.creditCardDebt,
                    personalLoanDebt: this.personalLoanDebt,
                    medicalDebt: this.medicalDebt,
                    otherDebt: this.otherDebt,
                    debtSettlementRate: this.debtSettlementRate,
                    debtSettlementFees: this.debtSettlementFees,
                    creditCardMinimumPaymentPercentage: this.creditCardMinimumPaymentPercentage,
                    personalLoanMinimumPaymentPercentage: this.personalLoanMinimumPaymentPercentage,
                    creditcardAPRPercentage: this.creditcardAprPercentage,
                    personalLoanAPRPercentage: this.personalLoanAprPercentage,
                    personalLoanRemainingTerm: this.personalLoanRemainingTerm,
                    homeEquityAPRPercentage: this.homeEquityAprPercentage,
                    homeEquityTerm: this.homeEquityTerm,
                    rentOrOwnSelection: this.rentOrOwnSelection,
                    isOtherDebtVisible: this.isOtherDebtVisible === '1',
                },
                Section3HeadersAndSubHeaders: {
                    section3HeaderText: this.section3HeaderText,
                    section3SubHeaderText: this.section3SubHeaderText,
                    section3Tab1HeaderText: this.section3Tab1HeaderText,
                    section3Tab2HeaderText: this.section3Tab2HeaderText,
                    section3Tab3HeaderText: this.section3Tab3HeaderText,
                },
                Section4HeadersAndSubHeaders: {
                    section4HeaderText: this.section4HeaderText,
                    section4SubHeaderText: this.section4SubHeaderText,
                    section4SeeDetailsButtonText: this.section4SeeDetailsButtonText,
                    section4TableHeaderText1: this.section4TableHeaderText1,
                    section4TableHeaderText2: this.section4TableHeaderText2,
                    section4TableHeaderText3: this.section4TableHeaderText3,
                    section4TableHeaderText4: this.section4TableHeaderText4,
                    section4TableHeaderText5: this.section4TableHeaderText5,
                    section4TableCurrentDebtCoveredHeaderText: this.section4TableCurrentDebtCoveredHeaderText,
                    mobSection4TableHeaderText1: this.mobSection4TableHeaderText1,
                    mobSection4TableHeaderText2: this.mobSection4TableHeaderText2,
                    mobSection4TableHeaderText3: this.mobSection4TableHeaderText3,
                    section4SubTableHeaderText1: this.section4SubTableHeaderText1,
                    section4SubTableHeaderText2: this.section4SubTableHeaderText2,
                    section4SubTableHeaderText3: this.section4SubTableHeaderText3,
                    section4SubTableHeaderText4: this.section4SubTableHeaderText4,
                },
            };
        },
        mounted() {
            this.eventTriggered = true;
        },
        methods: {
            getAPIData(data) {
                this.DataFromAPI = data;
                this.formatDataForSections(data);
            },
            dataLoaded(isDataLoaded) {
                this.isDataLoaded = isDataLoaded;
            },
            seeDetailsClicked(data) {
                this.DialData = data;
            },
        },
        components: {
            DebtAdvisorOptions,
            DebtAdvisorTabular,
            DebtAdvisorTabbedSection,
        },
        mixins: [
            BaseMixin,
            DebtManagementAdvisorMixin,
        ],
    };

</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
